import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

interface AdminAuthContextType {
  isAdminLoggedIn: boolean;
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  loginAdmin: (email: string, password: string) => Promise<boolean>;
  logoutAdmin: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider = ({ children }: AdminAuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Check if user is admin
          setTimeout(async () => {
            try {
              const { data: adminData, error } = await supabase
                .from('admin_users')
                .select('*')
                .eq('user_id', session.user.id)
                .single();
              
              setIsAdminLoggedIn(!error && !!adminData);
            } catch (error) {
              console.error('Error checking admin status:', error);
              setIsAdminLoggedIn(false);
            } finally {
              setIsLoading(false);
            }
          }, 0);
        } else {
          setIsAdminLoggedIn(false);
          setIsLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Check if user is admin
        setTimeout(async () => {
          try {
            const { data: adminData, error } = await supabase
              .from('admin_users')
              .select('*')
              .eq('user_id', session.user.id)
              .single();
            
            setIsAdminLoggedIn(!error && !!adminData);
          } catch (error) {
            console.error('Error checking admin status:', error);
            setIsAdminLoggedIn(false);
          } finally {
            setIsLoading(false);
          }
        }, 0);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Log failed login attempt
        await supabase.rpc('log_security_event', {
          p_user_id: null,
          p_event_type: 'admin_login_failed',
          p_event_details: { email, error: error.message },
          p_success: false
        });
        console.error('Login error:', error);
        return false;
      }

      // Verify the user is an admin using the enhanced function
      if (data.user) {
        const { data: isAdmin, error: adminError } = await supabase.rpc('verify_admin_access', {
          user_uuid: data.user.id
        });

        if (adminError || !isAdmin) {
          await supabase.auth.signOut();
          
          // Log unauthorized access attempt
          await supabase.rpc('log_security_event', {
            p_user_id: data.user.id,
            p_event_type: 'unauthorized_admin_access',
            p_event_details: { email },
            p_success: false
          });
          
          console.error('Utilisateur non autorisé');
          return false;
        }

        // Log successful admin login
        await supabase.rpc('log_security_event', {
          p_user_id: data.user.id,
          p_event_type: 'admin_login_success',
          p_event_details: { email },
          p_success: true
        });
      }
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      
      // Log the error
      await supabase.rpc('log_security_event', {
        p_user_id: null,
        p_event_type: 'admin_login_error',
        p_event_details: { error: error instanceof Error ? error.message : 'Erreur inconnue' },
        p_success: false
      });
      
      return false;
    }
  };

  const logoutAdmin = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AdminAuthContext.Provider value={{ 
      isAdminLoggedIn, 
      user, 
      session, 
      isLoading, 
      loginAdmin,
      logoutAdmin 
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};